<template>
	<div
		class="offer-content-block"
		:class="[
			{ [`offer-content-block--${skin}`]: skin },
			{
				[`offer-content-block--subtype-${subtype}`]: subtype,
			},
		]"
	>
		<slot name="content"></slot>
	</div>
</template>

<script setup lang="ts">
interface Props {
	skin?: string;
	subtype?: string;
}

const { skin, subtype } = defineProps<Props>();
</script>

<style lang="scss" scoped>
.offer-content-block {
	position: relative;
	flex: 1 1 auto;
	z-index: 2; /* (content < ribbon) = 2 */
	margin-top: -0.25rem; // for smooth connection to the ribbon
	width: 100%;
	overflow: hidden;

	&::before {
		position: absolute;
		top: -1rem;
		left: -0.75rem;
		z-index: 1;
		filter: blur(0.5rem); // for natural look
		background: linear-gradient(
			0deg,
			rgba(0, 0, 0, 0) 0%,
			rgba(0, 0, 0, 0.7) 75%
		);
		width: calc(100% + 1rem);
		height: 6rem;
		content: "";
	}

	&::after {
		position: absolute;
		top: 0;
		left: 0;
		opacity: 0.6;
		z-index: 0;
		// NOTE: Color-burn blending is better for dark colors
		mix-blend-mode: color-burn;
		background: repeat center;
		@include background(
			url($path-images + "backgrounds/textures/marble-texture-bg.avif"),
			40%,
			center,
			repeat
		);
		width: 100%;
		height: 100%;
		content: "";
	}

	&--subtype {
		&-welcome_back_offer {
			margin-top: -3.125rem;
		}
	}

	&--skin_01,
	&--event_skin_fishing_03,
	&--event_skin_fishing_05,
	&--skin_people_02,
	&--skin_coin_02,
	&--event_skin_solarfair_02,
	&--regular_starter_skin_golden,
	&--event_skin_hunting_04,
	&--event_skin_halloween_09,
	&--event_skin_pirates_07,
	&--event_skin_pirates_09 {
		background: linear-gradient(
			0deg,
			rgba(108, 64, 38, 1) 0%,
			rgba(198, 153, 57, 1) 100%
		);

		:deep(.rewards-grid__item) {
			border-image-source: linear-gradient(
				90deg,
				rgba(205, 145, 45, 0) 0%,
				rgba(205, 145, 45, 0.8) 25%,
				rgba(205, 145, 45, 0.8) 75%,
				rgba(205, 145, 45, 0) 100%
			);

			.rewards-grid__label::after {
				background: rgba(112, 62, 16, 0.8);
				content: "";
			}

			.bidirectional-text-label {
				.fill-solid .base-color {
					fill: #ad2f35;
				}
			}
		}
	}

	&--skin_02,
	&--event_skin_fishing_02,
	&--event_skin_fishing_07,
	&--skin_resource_02,
	&--skin_level_up_02,
	&--event_skin_firefest_01,
	&--event_skin_firefest_11,
	&--event_skin_solarfair_07,
	&--event_skin_solarfair_11,
	&--regular_starter_skin_01,
	&--event_skin_hunting_11,
	&--event_skin_pirates_06,
	&--event_skin_pirates_11 {
		background: linear-gradient(
			0deg,
			rgba(19, 70, 85, 1) 0%,
			rgba(63, 154, 180, 1) 100%
		);

		:deep(.rewards-grid__item) {
			border-image-source: linear-gradient(
				90deg,
				rgba(205, 145, 45, 0) 0%,
				rgba(205, 145, 45, 0.8) 25%,
				rgba(205, 145, 45, 0.8) 75%,
				rgba(205, 145, 45, 0) 100%
			);

			.rewards-grid__label::after {
				background: rgba(18, 52, 61, 0.8);
				content: "";
			}

			.bidirectional-text-label {
				.fill-solid .base-color {
					fill: #a96031;
				}
			}
		}
	}

	&--skin_03,
	&--regular_starter_skin_02 {
		background: linear-gradient(
			0deg,
			rgba(53, 38, 71, 1) 0%,
			rgba(117, 97, 175, 1) 100%
		);

		:deep(.rewards-grid__item) {
			border-image-source: linear-gradient(
				90deg,
				rgba(205, 145, 45, 0) 0%,
				rgba(205, 145, 45, 0.8) 25%,
				rgba(205, 145, 45, 0.8) 75%,
				rgba(205, 145, 45, 0) 100%
			);

			.rewards-grid__label::after {
				background: rgba(62, 33, 101, 0.8);
				content: "";
			}

			.bidirectional-text-label {
				.fill-solid .base-color {
					fill: #b64883;
				}
			}
		}
	}

	&--skin_04,
	&--event_skin_fishing_04,
	&--event_skin_fishing_06,
	&--skin_exploration_01,
	&--event_skin_firefest_04,
	&--event_skin_firefest_06,
	&--regular_starter_skin_03 {
		background: linear-gradient(
			0deg,
			rgba(28, 74, 66, 1) 0%,
			rgba(34, 181, 145, 1) 100%
		);

		:deep(.rewards-grid__item) {
			border-image-source: linear-gradient(
				90deg,
				rgba(205, 145, 45, 0) 0%,
				rgba(205, 145, 45, 0.8) 25%,
				rgba(205, 145, 45, 0.8) 75%,
				rgba(205, 145, 45, 0) 100%
			);

			.rewards-grid__label::after {
				background: rgba(28, 74, 66, 0.8);
				content: "";
			}

			.bidirectional-text-label {
				.fill-solid .base-color {
					fill: #bc4f22;
				}
			}
		}
	}

	&--skin_05,
	&--skin_storage_01,
	&--skin_gem_02,
	&--event_skin_firefest_12,
	&--regular_starter_skin_04,
	&--event_skin_halloween_07,
	&--event_skin_halloween_11,
	&--event_skin_pirates_08 {
		background: linear-gradient(
			0deg,
			rgba(32, 32, 32, 1) 0%,
			rgba(94, 75, 72, 1) 100%
		);

		:deep(.rewards-grid__item) {
			border-image-source: linear-gradient(
				90deg,
				rgba(205, 145, 45, 0) 0%,
				rgba(205, 145, 45, 0.8) 25%,
				rgba(205, 145, 45, 0.8) 75%,
				rgba(205, 145, 45, 0) 100%
			);

			.rewards-grid__label::after {
				background: rgba(32, 32, 32, 0.8);
				content: "";
			}

			.bidirectional-text-label {
				.fill-solid .base-color {
					fill: #ad2f35;
				}
			}
		}
	}

	&--skin_06,
	&--event_skin_fishing_01,
	&--event_skin_fishing_08,
	&--skin_gem_01,
	&--event_skin_firefest_08,
	&--event_skin_solarfair_01,
	&--regular_starter_skin_05,
	&--event_skin_hunting_10,
	&--event_skin_halloween_01,
	&--event_skin_halloween_12,
	&--event_skin_pirates_01 {
		background: linear-gradient(
			0deg,
			rgba(125, 58, 53, 1) 0%,
			rgba(225, 131, 86, 1) 100%
		);

		:deep(.rewards-grid__item) {
			border-image-source: linear-gradient(
				90deg,
				rgba(205, 145, 45, 0) 0%,
				rgba(205, 145, 45, 0.8) 25%,
				rgba(205, 145, 45, 0.8) 75%,
				rgba(205, 145, 45, 0) 100%
			);

			.rewards-grid__label::after {
				background: rgba(75, 17, 9, 0.8);
				content: "";
			}

			.bidirectional-text-label {
				.fill-solid .base-color {
					fill: #40acb7;
				}
			}
		}
	}

	&--skin_07,
	&--regular_starter_skin_06 {
		background: linear-gradient(
			0deg,
			rgba(71, 93, 27, 1) 0%,
			rgba(128, 159, 28, 1) 100%
		);

		:deep(.rewards-grid__item) {
			border-image-source: linear-gradient(
				90deg,
				rgba(205, 145, 45, 0) 0%,
				rgba(205, 145, 45, 0.8) 25%,
				rgba(205, 145, 45, 0.8) 75%,
				rgba(205, 145, 45, 0) 100%
			);

			.rewards-grid__label::after {
				background: rgba(51, 72, 9, 0.8);
				content: "";
			}

			.bidirectional-text-label {
				.fill-solid .base-color {
					fill: #8d57ab;
				}
			}
		}
	}

	&--skin_08,
	&--event_skin_firefest_02,
	&--event_skin_solarfair_03,
	&--regular_starter_skin_07,
	&--event_skin_hunting_12,
	&--event_skin_halloween_04 {
		background: linear-gradient(
			0deg,
			rgba(62, 44, 65, 1) 0%,
			rgba(162, 68, 141, 1) 100%
		);

		:deep(.rewards-grid__item) {
			border-image-source: linear-gradient(
				90deg,
				rgba(205, 145, 45, 0) 0%,
				rgba(205, 145, 45, 0.8) 25%,
				rgba(205, 145, 45, 0.8) 75%,
				rgba(205, 145, 45, 0) 100%
			);

			.rewards-grid__label::after {
				background: rgba(68, 15, 74, 0.8);
				content: "";
			}

			.bidirectional-text-label {
				.fill-solid .base-color {
					fill: #ad2f35;
				}
			}
		}
	}

	&--skin_09,
	&--skin_resource_01,
	&--skin_level_up_01,
	&--event_skin_solarfair_08,
	&--regular_starter_skin_08,
	&--event_skin_hunting_01,
	&--event_skin_hunting_06 {
		background: linear-gradient(
			0deg,
			rgba(108, 64, 38, 1) 0%,
			rgba(198, 153, 57, 1) 100%
		);

		:deep(.rewards-grid__item) {
			border-image-source: linear-gradient(
				90deg,
				rgba(205, 145, 45, 0) 0%,
				rgba(205, 145, 45, 0.8) 25%,
				rgba(205, 145, 45, 0.8) 75%,
				rgba(205, 145, 45, 0) 100%
			);

			.rewards-grid__label::after {
				background: rgba(112, 62, 16, 0.8);
				content: "";
			}

			.bidirectional-text-label {
				.fill-solid .base-color {
					fill: #1490b3;
				}
			}
		}
	}

	&--skin_10,
	&--skin_storage_02,
	&--skin_food_02,
	&--event_skin_firefest_05,
	&--event_skin_firefest_10,
	&--event_skin_solarfair_05,
	&--event_skin_solarfair_10,
	&--event_skin_hunting_02,
	&--event_skin_hunting_07,
	&--event_skin_halloween_02,
	&--event_skin_halloween_06,
	&--event_skin_pirates_02 {
		background: linear-gradient(
			0deg,
			rgba(53, 77, 7, 1) 0%,
			rgba(104, 133, 43, 1) 100%
		);

		:deep(.rewards-grid__item) {
			border-image-source: linear-gradient(
				90deg,
				rgba(205, 145, 45, 0) 0%,
				rgba(205, 145, 45, 0.8) 25%,
				rgba(205, 145, 45, 0.8) 75%,
				rgba(205, 145, 45, 0) 100%
			);

			.rewards-grid__label::after {
				background: rgba(41, 62, 11, 0.8);
				content: "";
			}

			.bidirectional-text-label {
				.fill-solid .base-color {
					fill: #a04d00;
				}
			}
		}
	}

	&--skin_11,
	&--skin_exploration_02,
	&--event_skin_firefest_07,
	&--event_skin_hunting_09,
	&--event_skin_halloween_05 {
		background: linear-gradient(
			0deg,
			rgba(32, 32, 32, 1) 0%,
			rgba(94, 75, 72, 1) 100%
		);

		:deep(.rewards-grid__item) {
			border-image-source: linear-gradient(
				90deg,
				rgba(205, 145, 45, 0) 0%,
				rgba(205, 145, 45, 0.8) 25%,
				rgba(205, 145, 45, 0.8) 75%,
				rgba(205, 145, 45, 0) 100%
			);

			.rewards-grid__label::after {
				background: rgba(32, 32, 32, 0.8);
				content: "";
			}

			.bidirectional-text-label {
				.fill-solid .base-color {
					fill: #704dc0;
				}
			}
		}
	}

	&--skin_12,
	&--skin_people_01 {
		background: linear-gradient(
			0deg,
			rgba(41, 68, 108, 1) 0%,
			rgba(91, 140, 201, 1) 100%
		);

		:deep(.rewards-grid__item) {
			border-image-source: linear-gradient(
				90deg,
				rgba(205, 145, 45, 0) 0%,
				rgba(205, 145, 45, 0.8) 25%,
				rgba(205, 145, 45, 0.8) 75%,
				rgba(205, 145, 45, 0) 100%
			);

			.rewards-grid__label::after {
				background: rgba(28, 52, 87, 0.8);
				content: "";
			}

			.bidirectional-text-label {
				.fill-solid .base-color {
					fill: #976514;
				}
			}
		}
	}

	&--skin_13,
	&--skin_food_01,
	&--skin_building_02,
	&--event_skin_firefest_03,
	&--event_skin_firefest_09,
	&--event_skin_solarfair_09,
	&--event_skin_hunting_03,
	&--event_skin_halloween_08,
	&--event_skin_pirates_03,
	&--event_skin_pirates_05,
	&--event_skin_pirates_12 {
		background: linear-gradient(
			0deg,
			rgba(109, 47, 13, 1) 0%,
			rgba(226, 123, 72, 1) 100%
		);

		:deep(.rewards-grid__item) {
			border-image-source: linear-gradient(
				90deg,
				rgba(205, 145, 45, 0) 0%,
				rgba(205, 145, 45, 0.8) 25%,
				rgba(205, 145, 45, 0.8) 75%,
				rgba(205, 145, 45, 0) 100%
			);

			.rewards-grid__label::after {
				background: rgba(89, 33, 17, 0.8);
				content: "";
			}

			.bidirectional-text-label {
				.fill-solid .base-color {
					fill: #5b59a3;
				}
			}
		}
	}

	&--skin_14,
	&--skin_coin_01,
	&--event_skin_solarfair_06,
	&--event_skin_hunting_05 {
		background: linear-gradient(
			0deg,
			rgba(17, 43, 26, 1) 0%,
			rgba(67, 82, 71, 1) 100%
		);

		:deep(.rewards-grid__item) {
			border-image-source: linear-gradient(
				90deg,
				rgba(205, 145, 45, 0) 0%,
				rgba(205, 145, 45, 0.8) 25%,
				rgba(205, 145, 45, 0.8) 75%,
				rgba(205, 145, 45, 0) 100%
			);

			.rewards-grid__label::after {
				background: rgba(17, 43, 26, 0.8);
				content: "";
			}

			.bidirectional-text-label {
				.fill-solid .base-color {
					fill: #976514;
				}
			}
		}
	}

	&--skin_15,
	&--skin_building_01,
	&--skin_gem_03 {
		background: linear-gradient(
			0deg,
			rgba(11, 72, 84, 1) 0%,
			rgba(62, 192, 216, 1) 100%
		);

		:deep(.rewards-grid__item) {
			border-image-source: linear-gradient(
				90deg,
				rgba(205, 145, 45, 0) 0%,
				rgba(205, 145, 45, 0.8) 25%,
				rgba(205, 145, 45, 0.8) 75%,
				rgba(205, 145, 45, 0) 100%
			);

			.rewards-grid__label::after {
				background: rgba(9, 59, 69, 0.8);
				content: "";
			}

			.bidirectional-text-label {
				.fill-solid .base-color {
					fill: #73751c;
				}
			}
		}
	}

	&--skin_16,
	&--skin_gem_04,
	&--event_skin_solarfair_04,
	&--event_skin_solarfair_12,
	&--event_skin_hunting_08,
	&--event_skin_halloween_03,
	&--event_skin_halloween_10,
	&--event_skin_pirates_04,
	&--event_skin_pirates_10 {
		background: linear-gradient(
			0deg,
			rgba(121, 7, 7, 1) 0%,
			rgba(228, 106, 106, 1) 100%
		);

		:deep(.rewards-grid__item) {
			border-image-source: linear-gradient(
				90deg,
				rgba(205, 145, 45, 0) 0%,
				rgba(205, 145, 45, 0.8) 25%,
				rgba(205, 145, 45, 0.8) 75%,
				rgba(205, 145, 45, 0) 100%
			);

			.rewards-grid__label::after {
				background: rgba(100, 11, 11, 0.8);
				content: "";
			}

			.bidirectional-text-label {
				.fill-solid .base-color {
					fill: #1490b3;
				}
			}
		}
	}

	&--event_pass_skin_easter_01,
	&--event_pass_skin_fishing_01,
	&--event_pass_skin_firefest_01,
	&--event_pass_skin_hunting_01,
	&--event_pass_skin_halloween_01 {
		background: linear-gradient(
			0deg,
			rgb(53, 38, 71) 0%,
			rgb(117, 97, 175) 100%
		);
		padding: 1.5rem 0 1rem;
	}

	&--event_skin_easter_01,
	&--event_skin_easter_05,
	&--event_skin_easter_09 {
		background: linear-gradient(
			0deg,
			rgba(125, 58, 53, 1) 0%,
			rgba(225, 131, 86, 1) 100%
		);

		:deep(.rewards-grid__item) {
			border-image-source: linear-gradient(
				90deg,
				rgba(205, 145, 45, 0) 0%,
				rgba(205, 145, 45, 0.8) 25%,
				rgba(205, 145, 45, 0.8) 75%,
				rgba(205, 145, 45, 0) 100%
			);

			.rewards-grid__label::after {
				background: rgba(75, 17, 9, 0.8);
				content: "";
			}

			.bidirectional-text-label {
				.fill-solid .base-color {
					fill: #40acb7;
				}
			}
		}
	}

	&--event_skin_easter_02,
	&--event_skin_easter_06,
	&--event_skin_easter_10 {
		background: linear-gradient(
			0deg,
			rgba(19, 70, 85, 1) 0%,
			rgba(63, 154, 180, 1) 100%
		);

		:deep(.rewards-grid__item) {
			border-image-source: linear-gradient(
				90deg,
				rgba(205, 145, 45, 0) 0%,
				rgba(205, 145, 45, 0.8) 25%,
				rgba(205, 145, 45, 0.8) 75%,
				rgba(205, 145, 45, 0) 100%
			);

			.rewards-grid__label::after {
				background: rgba(18, 52, 61, 0.8);
				content: "";
			}

			.bidirectional-text-label {
				.fill-solid .base-color {
					fill: #a96031;
				}
			}
		}
	}

	&--event_skin_easter_03,
	&--event_skin_easter_07,
	&--event_skin_easter_11 {
		background: linear-gradient(
			0deg,
			rgba(108, 64, 38, 1) 0%,
			rgba(198, 153, 57, 1) 100%
		);

		:deep(.rewards-grid__item) {
			border-image-source: linear-gradient(
				90deg,
				rgba(205, 145, 45, 0) 0%,
				rgba(205, 145, 45, 0.8) 25%,
				rgba(205, 145, 45, 0.8) 75%,
				rgba(205, 145, 45, 0) 100%
			);

			.rewards-grid__label::after {
				background: rgba(112, 62, 16, 0.8);
				content: "";
			}

			.bidirectional-text-label {
				.fill-solid .base-color {
					fill: #ad2f35;
				}
			}
		}
	}

	&--event_skin_easter_04,
	&--event_skin_easter_08,
	&--event_skin_easter_12 {
		background: linear-gradient(
			0deg,
			rgba(71, 93, 27, 1) 0%,
			rgba(128, 159, 28, 1) 100%
		);

		:deep(.rewards-grid__item) {
			border-image-source: linear-gradient(
				90deg,
				rgba(205, 145, 45, 0) 0%,
				rgba(205, 145, 45, 0.8) 25%,
				rgba(205, 145, 45, 0.8) 75%,
				rgba(205, 145, 45, 0) 100%
			);

			.rewards-grid__label::after {
				background: rgba(51, 72, 9, 0.8);
				content: "";
			}

			.bidirectional-text-label {
				.fill-solid .base-color {
					fill: #8d57ab;
				}
			}
		}
	}

	&--piggy_bank_gem_skin_01 {
		&--progress {
			background: linear-gradient(
				0deg,
				rgba(32, 44, 89, 1) 0%,
				rgba(83, 131, 207, 1) 100%
			);
		}

		&--full {
			background: linear-gradient(
				0deg,
				rgba(108, 64, 38, 1) 0%,
				rgba(198, 153, 57, 1) 100%
			);
		}
	}

	&--provision_royal_skin_01 {
		background: linear-gradient(
			0deg,
			rgba(30, 59, 62, 1) 0%,
			rgba(43, 115, 121, 1) 100%
		);
	}
}
</style>
